import { Role, RoleType } from "@the-real-insight/tri-model";

/** Convert display name to role identifier: first letter lowercase, whitespace and non-identifier chars to _. */
export function nameToRoleId(name: string): string {
    if (!name || typeof name !== "string") return "";
    const s = name.trim();
    if (!s) return "";
    const id = s
        .replace(/[^a-zA-Z0-9_]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");
    if (!id) return "";
    return id.charAt(0).toLowerCase() + id.slice(1);
}

/**
 * A role the customer defined themselves — the "Eigene Rollen" group of the Teamverwaltung.
 *
 * The other two groups are deliberately excluded:
 *
 * - **Systemrollen** are `parameterizableRole`s scoped to `Customer` (Administration, Billing …).
 *   They describe platform permissions, not what someone does in the business.
 * - **Legacy** are plain roles belonging to a *different* tenant (Compliance, Datenanalyse,
 *   REAL GAIN Advisory Board …) that a customer still carries. They cannot be deleted in the UI and
 *   are not maintained.
 *
 * Only the customer's own roles are meaningful as departments — which is why the tender context
 * (search profiles, the board's Bereich filter, Weitergabe) shows nothing else.
 *
 * This is the single definition of the rule; `classifyRoles` in components/RoleList.tsx sorts the
 * Teamverwaltung tree with it, so the two surfaces cannot disagree about what "eigene Rolle" means.
 */
export const isOwnRole = (role: Role, customerId: string): boolean =>
    role?.type === RoleType.role && (role as any)._tenant === customerId;

/**
 * The customer's own roles as `{key, text}` for a dropdown — deduplicated by id and sorted by name.
 * Roles with no id or no display name are skipped rather than rendered as a blank entry.
 */
export const ownRoleOptions = (
    roles: Role[],
    customerId: string
): { key: string; text: string }[] => {
    const seen = new Set<string>();
    const items: { key: string; text: string }[] = [];
    for (const role of roles) {
        if (!isOwnRole(role, customerId)) continue;
        const id = (role as any)._id;
        const name = role.getRoleName();
        if (!id || !name || seen.has(String(id))) continue;
        seen.add(String(id));
        items.push({ key: String(id), text: name });
    }
    return items.sort((a, b) => a.text.localeCompare(b.text));
};

/** Parse raw API data into Role instances, skipping invalid entries. */
export const parseRoles = (data: any[]): Role[] =>
    (data || [])
        .filter((r: any) => r?._id && r?.name)
        .map((r: any) => {
            try {
                return new Role(r);
            } catch {
                return null;
            }
        })
        .filter((r): r is Role => r !== null);
